<!--
Search component to select origin / destination.
Usage: <Search name="text" placeholder="placeholder" otherLocation="origin/destination"/>
@author crm4f
-->
<template>
  <div>
    <div class="form-group has-feedback w-100" role="search">
      <div class="input-group mb-3">
        <input type="text" @keypress="$event.key == 'Enter' && canSubmit() ? $emit('submit', getResultSet()) : null" @change="canSubmit() ? $emit('submit', getResultSet()) : null" v-model="filter" :list="name + 'List'" :id="name + 'Search'" class="form-control" :placeholder="placeholder" autocomplete="off" spellcheck="false" aria-label="End point">      
        <div class="input-group-append">
          <button @click="filter = ''; $emit('clear')" type="button" class="h-100 input-group-text" id="basic-addon1"><font-awesome-icon class="clearButton" icon="fa-solid fa-x" /></button>
        </div>
        <div class="results" v-if="results.value != []">
          <datalist :id="name + 'List'">
            <option v-bind:key="index" v-for="result,index in results" :value="result[1]"></option>
          </datalist>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, watch, defineProps } from 'vue'
import { useRoute } from 'vue-router'
import axios from 'axios'

const route = useRoute()

const props = defineProps({
  name: {
    type: String,
    required: true
  }, 
  placeholder: {
    type: String,
    required: true
  },
  otherLocation: {
    required: true
  },
})

const query = ref('')
const filter = ref('')
const results = ref([])
const items = ref([])
const aliases = ref([])

const CURRENT_LOCATION = 'Current Location'

onMounted(() => {
  axios.get('map/aliases', { withCredentials: false })
    .then(response => {
      aliases.value = response.data
  })
  checkUrl()
})

function checkUrl () {
  if (window.location.href.indexOf('?to') > -1 && props.name == 'destination') {
    axios.get('map/locations/' + route.query.to, { withCredentials: false })
    .then(response => {
      filter.value = response.data.locationName
    })
  } 
  if (window.location.href.indexOf('&from') > -1 && props.name == 'origin') {
    axios.get('map/locations/' + route.query.from, { withCredentials: false })
    .then(response => {
      filter.value = response.data.locationName
    })
  }
}

watch(filter, async (value) => {
  if (query.value !== value.substring(0, 3)) {
    // First 3 chars has changed to re-run server query.
    query.value = value.substring(0, 3)
    await runQuery()
  }
  results.value = []
  if (props.name == 'origin') {
    results.value.push([CURRENT_LOCATION, CURRENT_LOCATION])
  }
  items.value.forEach((item) => {
    if (item[1].toLowerCase().indexOf(filter.value.toLowerCase()) >= 0) {
      if (props.otherLocation !== null) {
        if (showLocation(item, props.otherLocation)) {
          results.value.push(item)
        }
      } else {
        results.value.push(item)
      }
    } else {
      for (var i = 0; i < aliases.value.length; i++) {
        if (aliases.value[i].aliasText.toLowerCase().indexOf(filter.value.toLowerCase()) >= 0 && item[1].indexOf(aliases.value[i].buildingName) >= 0) {
          if (props.otherLocation !== null) {
            if (showLocation(item, props.otherLocation)) {
              results.value.push(item)
            }
            break
          } else {
            results.value.push(item)
          }
        }
      }
    }
  })
})

/**
   * Used to filter illogical location suggestions.
   * e.g. don't show building a if room b is in that building.
   * This stops spurious direction requests.
   * 
   * @param a location suggested in a search request
   * @param b location selected from another location search.
   * @return true if b is set to a location and we 
*/
function showLocation(a, b) {
  
  var show = false
  
  if(b != null) {
    // Other location has been selected.
    var aId = a[0]
    var bId = b.locationId
    if(aId != bId) {
    
      // Other location does not match current so check.
      if(aId.substr(0,3) != bId.substr(0, 3)) {
        // Location in different building so show.
        show = true
      } else {
        // Location in same building so check if room-to-room directions available.
        if(b.roomToRoom) {
          // Room-to-room directions available so check that both locations are rooms.
          if(aId.length > 3 && bId.length > 3) {
            show = true
          }
        }
      }
    }
  }
      
  return show
}

async function runQuery () {
  if (query.value.length === 3) {
    const safeQuery = encodeURIComponent(query.value)
    await axios.get('/map/locations?q=' + safeQuery, { withCredentials: false })
      .then(response => {
        items.value = response.data
        document.getElementById(props.name + 'Search').focus()
      })
  } else if (query.value.length < 3 && items.value.length > 0) {
    items.value = []
  }
}

function canSubmit() {
  return results.value.find((result) => result[1] == filter.value) != undefined
}

// returns the [id, building] given the building. used to emit the building id.
function getResultSet() {
  for (let i = 0; i < results.value.length; i++) {
    if (results.value[i][1] == filter.value) {
      return results.value[i]
    }
  }
}

</script>

<style scoped>
.disabled {
  color: #babbbc;
}

.active {
  color: #003865
}

.clearButton {
  color: #003865
}

</style>